import { ComposeEmailDialog, ContactEmails } from "../components/email-dialog";
import { AddressActions } from "../components/address-actions";
import { CONTACT_CATEGORY_LABELS, formatPhone, type ContactDetail, type ContactListItem } from "@nmt/shared";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { del, errorMessage, get, patch, post } from "../api";
import { FilesPanel } from "../components/files";
import { TasksPanel } from "../components/schedule";
import { SubDocuments, SubcontractorPanels } from "./WorkOrders";
import { CommsPanel } from "./Comms";
import { ContactDialog, NewJobDialog } from "../components/forms";
import { History, Notes, RelatedContacts } from "../components/records";
import { Alert, Badge, Button, Dialog, Empty, Field, Loading, PageHeader, Panel, PanelHeader, SelectField, StageBadge, Tabs, addressLine, ago } from "../components/ui";
import { useSession } from "../session";
import { useRememberedTab } from "../components/use-tab";
import { RichText } from "../components/rich-text";

type Page = { items: ContactListItem[]; more: boolean };
const PAGE = 50;

/** Everyone in one list: customers, subcontractors, vendors, adjusters... filtered by type. */
export function ContactsPage() {
  const { can, me } = useSession();
  const [params, setParams] = useSearchParams();
  const typeFilter = params.get("type") ?? "";
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [adding, setAdding] = useState(false);
  const [fromQb, setFromQb] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(q.trim()), 250);
    return () => window.clearTimeout(t);
  }, [q]);

  const list = useInfiniteQuery({
    queryKey: ["contacts", debounced, typeFilter],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams({ limit: String(PAGE), offset: String(pageParam) });
      if (debounced) qs.set("q", debounced);
      // "category:customer" filters by category; anything else is a type ID.
      if (typeFilter.startsWith("category:")) qs.set("category", typeFilter.slice(9));
      else if (typeFilter) qs.set("typeId", typeFilter);
      return get<Page>(`/api/contacts?${qs}`);
    },
    getNextPageParam: (last, pages) => (last.more ? pages.length * PAGE : undefined),
  });
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];
  const defaultType = typeFilter && !typeFilter.startsWith("category:") ? typeFilter : undefined;

  return (
    <div>
      <PageHeader
        title="Contacts"
        description="Customers, subcontractors, vendors, adjusters… Customers can have any number of jobs, each at its own address."
        action={
          can("customers.edit") && (
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setFromQb(true)}>Vendors from QuickBooks…</Button>
              <Button variant="primary" onClick={() => setAdding(true)}>
                + New contact
              </Button>
            </div>
          )
        }
      />
      {fromQb && <QbVendorsImport onClose={() => setFromQb(false)} />}
      <div className="mb-4 grid gap-3 sm:grid-cols-[1fr_240px]">
        <div>
          <label htmlFor="contact-q" className="mb-1 block text-sm font-semibold text-ink-soft">
            Search
          </label>
          <input
            id="contact-q"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Name, company, phone, email, address"
            className="w-full rounded-[var(--radius-control)] border border-line bg-white px-3 py-2 focus:border-brand focus:ring-2 focus:ring-brand/20 focus:outline-none"
          />
        </div>
        <SelectField
          label="Type"
          value={typeFilter}
          onChange={(e) => setParams(e.target.value ? { type: e.target.value } : {}, { replace: true })}
        >
          <option value="">All contacts</option>
          <optgroup label="Groups">
            <option value="category:customer">All customers</option>
            <option value="category:subcontractor">All subcontractors</option>
            <option value="category:vendor">All vendors</option>
          </optgroup>
          <optgroup label="Types">
            {me?.contactTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </optgroup>
        </SelectField>
      </div>

      {list.isLoading ? (
        <Loading />
      ) : list.error ? (
        <Alert>{errorMessage(list.error)}</Alert>
      ) : !items.length ? (
        <Panel>
          <Empty>{debounced || typeFilter ? "No contacts match." : "No contacts yet."}</Empty>
        </Panel>
      ) : (
        <Panel className="overflow-x-auto">
          <table className="table-base">
            <thead>
              <tr>
                <th>Name</th>
                <th className="hidden sm:table-cell">Type</th>
                <th className="hidden sm:table-cell">Phone</th>
                <th className="hidden md:table-cell">Address</th>
                <th>Jobs</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="hover:bg-mist/50">
                  <td>
                    <Link to={`/contacts/${c.id}`} className="font-semibold hover:text-brand">
                      {c.displayName}
                    </Link>
                    {c.company && c.company !== c.displayName && <span className="ml-2 text-sm text-muted">{c.company}</span>}
                    <div className="text-sm text-muted sm:hidden">{[c.typeName, formatPhone(c.phone)].filter(Boolean).join(" · ")}</div>
                  </td>
                  <td className="hidden text-sm sm:table-cell">{c.typeName}</td>
                  <td className="hidden text-sm sm:table-cell">{formatPhone(c.phone)}</td>
                  <td className="hidden text-sm text-ink-soft md:table-cell">{[c.street, c.city].filter(Boolean).join(", ")}</td>
                  <td className="text-sm">
                    {c.category !== "customer" && !c.jobCount ? (
                      <span className="text-muted">—</span>
                    ) : c.openJobCount > 0 ? (
                      <Badge tone="brand">{c.openJobCount} open</Badge>
                    ) : (
                      <span className="text-muted">{c.jobCount || "None"}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
      {list.hasNextPage && (
        <div className="mt-4 flex justify-center">
          <Button onClick={() => list.fetchNextPage()} busy={list.isFetchingNextPage}>
            Show more
          </Button>
        </div>
      )}
      {adding && <ContactDialog defaultTypeId={defaultType} onClose={() => setAdding(false)} />}
    </div>
  );
}

type Tab = "overview" | "comms" | "history";

export function ContactDetailPage() {
  const { id = "" } = useParams();
  const { can } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useRememberedTab<Tab>("contact", ["overview", "comms", "history"], "overview");
  const [editing, setEditing] = useState(false);
  const [newJob, setNewJob] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const contact = useQuery({ queryKey: ["contact", id], queryFn: () => get<ContactDetail>(`/api/contacts/${id}`) });
  const remove = useMutation({
    mutationFn: () => del(`/api/contacts/${id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["contacts"] });
      navigate("/contacts");
    },
  });
  const restore = useMutation({
    mutationFn: () => post<ContactDetail>(`/api/contacts/${id}/restore`),
    onSuccess: (c) => qc.setQueryData(["contact", id], c),
  });

  if (contact.isLoading) return <Loading />;
  if (contact.error) return <Alert>{errorMessage(contact.error)}</Alert>;
  const c = contact.data!;
  const isCustomer = c.category === "customer";
  const address = addressLine(c);
  const personName = `${c.firstName} ${c.lastName}`.trim();

  return (
    <div className="flex flex-col gap-5">
      {c.deletedAt && (
        <Alert>
          {isCustomer ? "This customer and their jobs are in the recycle bin." : "This contact is in the recycle bin."}{" "}
          {can("records.delete") && (
            <button className="font-semibold underline" onClick={() => restore.mutate()}>
              Restore
            </button>
          )}
        </Alert>
      )}
      <Panel>
        <div className="flex flex-wrap items-start justify-between gap-4 px-5 py-5">
          <div className="min-w-0">
            <div className="text-sm text-muted">
              <Link to="/contacts" className="hover:text-ink">
                Contacts
              </Link>
              {" · "}
              <Link to={`/contacts?type=${c.typeId}`} className="hover:text-ink">
                {c.typeName}
              </Link>
              {c.kind === "commercial" && " · Business"}
            </div>
            <h1 className="page-title mt-1">{c.displayName}</h1>
            {c.kind === "commercial" && personName && <div className="text-ink-soft">Contact: {personName}</div>}
            {c.kind !== "commercial" && c.company && c.company !== c.displayName && <div className="text-ink-soft">{c.company}</div>}
            <div className="mt-2 flex flex-col gap-0.5 text-sm text-ink-soft">
              {c.phone && (
                <a href={`tel:${c.phone}`} className="hover:text-brand">
                  {formatPhone(c.phone)}
                </a>
              )}
              {c.phone2 && (
                <a href={`tel:${c.phone2}`} className="hover:text-brand">
                  {formatPhone(c.phone2)} <span className="text-muted">(other)</span>
                </a>
              )}
              {c.email &&
                (can("comms.send") ? (
                  <button className="text-left hover:text-brand" title="Email from the CRM" onClick={() => setEmailing(true)}>
                    {c.email}
                  </button>
                ) : (
                  <span>{c.email}</span>
                ))}
              {address && <span>{address}</span>}
            </div>
            {address && (
              <div className="mt-3 flex flex-wrap gap-2">
                <AddressActions address={address} contactId={c.id} />
              </div>
            )}
            {c.notes && <RichText className="mt-3 max-w-prose text-ink-soft" text={c.notes} />}
            <div className="mt-2 text-xs text-muted">{CONTACT_CATEGORY_LABELS[c.category].quickbooks}</div>
            {(c.category === "vendor" || c.category === "subcontractor") && <QbVendor contact={c} />}
          </div>
          {!c.deletedAt && (
            <div className="flex flex-wrap gap-2">
              {can("customers.edit") && <Button onClick={() => setEditing(true)}>Edit</Button>}
              {can("jobs.edit") && (isCustomer || c.category === "vendor" || c.category === "subcontractor") && (
                <Button variant="primary" onClick={() => setNewJob(true)}>
                  + New job
                </Button>
              )}
            </div>
          )}
        </div>
      </Panel>

      <Tabs<Tab>
        tabs={[
          { value: "overview", label: "Overview" },
          ...(can("comms.view") && c.phone ? [{ value: "comms" as const, label: "Communication" }] : []),
          { value: "history", label: "History" },
        ]}
        value={tab}
        onChange={setTab}
      />

      {tab === "overview" ? (
        <div className="grid gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <div className="flex flex-col gap-5">
            {(isCustomer || c.jobs.length > 0) && (
              <Panel>
                <PanelHeader title="Jobs" />
                {!c.jobs.length ? (
                  <Empty>No jobs yet.</Empty>
                ) : (
                  <ul>
                    {c.jobs.map((j) => (
                      <li key={j.id} className="border-b border-mist last:border-0">
                        <Link to={`/jobs/${j.id}`} className="flex items-center gap-3 px-5 py-3 hover:bg-mist/50">
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-semibold">
                              #{j.number} {j.workflowName}
                              {j.title && <span className="font-normal text-ink-soft"> — {j.title}</span>}
                            </div>
                            <div className="truncate text-xs text-muted">{[[j.street, j.city].filter(Boolean).join(", "), `updated ${ago(j.updatedAt)}`].filter(Boolean).join(" · ")}</div>
                          </div>
                          <StageBadge name={j.stageName} kind={j.stageKind} />
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>
            )}
            {c.onJobs.length > 0 && (
              <Panel>
                <PanelHeader title="On jobs" description={`Jobs ${c.displayName} is listed on.`} />
                <ul>
                  {c.onJobs.map((j) => (
                    <li key={j.id} className="border-b border-mist px-5 py-3 last:border-0">
                      <Link to={`/jobs/${j.id}`} className="font-semibold hover:text-brand">
                        #{j.number} {j.label}
                      </Link>
                      {j.role && <span className="ml-2 text-sm text-muted">{j.role}</span>}
                    </li>
                  ))}
                </ul>
              </Panel>
            )}
            {c.referrals.length > 0 && (
              <Panel>
                <PanelHeader title={`Referrals (${c.referrals.length})`} description={`Jobs ${c.displayName} sent us.`} />
                <ul>
                  {c.referrals.map((j) => (
                    <li key={j.id} className="border-b border-mist px-5 py-3 last:border-0">
                      <Link to={`/jobs/${j.id}`} className="font-semibold hover:text-brand">
                        #{j.number} {j.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </Panel>
            )}
            <Notes path={`/api/contacts/${id}/notes`} queryKey={["contact-notes", id]} showJob />
            {c.category === "subcontractor" ? <SubDocuments contactId={id} readOnly={!!c.deletedAt} /> : <FilesPanel owner={{ kind: "contact", id }} readOnly={!!c.deletedAt} />}
          </div>
          <div className="flex flex-col gap-5">
            {c.category === "subcontractor" && <SubcontractorPanels contactId={id} />}
            <TasksPanel contactId={id} />
            <RelatedContacts contacts={c.contacts} basePath={`/api/contacts/${id}`} invalidate={["contact", id]} />
            {c.linkedFrom.length > 0 && (
              <Panel>
                <PanelHeader title="Related to" />
                <ul>
                  {c.linkedFrom.map((x) => (
                    <li key={x.id} className="border-b border-mist px-5 py-3 last:border-0">
                      <Link to={`/contacts/${x.id}`} className="font-semibold hover:text-brand">
                        {x.displayName}
                      </Link>
                      {x.role && <span className="ml-2 text-sm text-muted">as their {x.role.toLowerCase()}</span>}
                    </li>
                  ))}
                </ul>
              </Panel>
            )}
            {can("records.delete") && !c.deletedAt && (
              <div className="flex justify-end">
                <Button
                  variant="danger"
                  busy={remove.isPending}
                  onClick={() => {
                    const what = c.jobCount ? `${c.displayName} and their ${c.jobCount} job(s)` : c.displayName;
                    if (window.confirm(`Move ${what} to the recycle bin?`)) remove.mutate();
                  }}
                >
                  Delete contact
                </Button>
              </div>
            )}
          </div>
        </div>
      ) : tab === "comms" ? (
        <div className="flex flex-col gap-5">
          <CommsPanel phone={c.phone} contact={{ id: c.id, name: c.displayName }} />
          {can("comms.view") && <ContactEmails contactId={c.id} />}
        </div>
      ) : (
        <History path={`/api/contacts/${id}/history`} queryKey={["contact-history", id]} />
      )}

      {emailing && <ComposeEmailDialog contactId={c.id} onClose={() => setEmailing(false)} />}
      {editing && <ContactDialog contact={c} onClose={() => setEditing(false)} onSaved={() => qc.invalidateQueries({ queryKey: ["contact-history", id] })} />}
      {newJob && <NewJobDialog customer={c} onClose={() => setNewJob(false)} />}
    </div>
  );
}

/**
 * A vendor's or sub's place in QuickBooks. Bills never create them there: it's done here on purpose
 * (or linked in Settings → QuickBooks → Find matches).
 */
function QbVendor({ contact: c }: { contact: ContactDetail }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const [linking, setLinking] = useState(false);
  const go = useMutation({ mutationFn: () => post<{ qbId: string; dryRun: boolean }>(`/api/contacts/${c.id}/quickbooks-vendor`), onSuccess: () => qc.invalidateQueries({ queryKey: ["contact", c.id] }) });
  const linked = c.qbVendorId && !c.qbVendorId.startsWith("pending:");
  const forget = useMutation({ mutationFn: (name: string) => patch(`/api/contacts/${c.id}`, { vendorAliases: c.vendorAliases.filter((a) => a !== name) }), onSuccess: () => qc.invalidateQueries({ queryKey: ["contact", c.id] }) });
  return (
    <div className="mt-1 text-xs">
      {c.vendorAliases.length > 0 && (
        <div className="mb-1 text-muted">
          Also printed as:{" "}
          {c.vendorAliases.map((a) => (
            <span key={a} className="mr-1 inline-flex items-center gap-1 rounded-full bg-mist px-2 py-0.5 text-ink-soft">
              {a}
              {can("customers.edit") && (
                <button aria-label={`Forget ${a}`} className="hover:text-danger" onClick={() => forget.mutate(a)}>
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      {linked ? (
        <span className="text-muted">In QuickBooks as vendor #{c.qbVendorId}.</span>
      ) : (
        <span className="text-muted">
          Not linked to a QuickBooks vendor yet, so their bills wait until it's linked (or created there, if it isn't in QuickBooks).{" "}
          {can("customers.edit") && (
            <>
              <button className="font-semibold text-brand hover:underline" onClick={() => setLinking(true)}>
                Link to a QuickBooks vendor
              </button>
              {" or "}
              <button className="font-semibold text-brand hover:underline disabled:text-muted" disabled={go.isPending} onClick={() => go.mutate()}>
                Create in QuickBooks
              </button>
            </>
          )}
          {go.data && <span className="ml-1">{go.data.dryRun ? "Recorded as a dry run (writes are off)." : "Created."}</span>}
          {go.error && <span className="ml-1 text-danger">{errorMessage(go.error)}</span>}
        </span>
      )}
      {linking && <LinkVendorDialog contact={c} onClose={() => setLinking(false)} />}
    </div>
  );
}

type QbVendorHit = { id: string; name: string; company: string; linkedTo: { id: string; name: string } | null };

/** Search QuickBooks's vendors and link one to this contact (only the CRM changes). */
function LinkVendorDialog({ contact: c, onClose }: { contact: ContactDetail; onClose: () => void }) {
  const qc = useQueryClient();
  const [q, setQ] = useState(c.company || c.lastName || c.displayName);
  const [debounced, setDebounced] = useState(q);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 300);
    return () => clearTimeout(t);
  }, [q]);
  const hits = useQuery({ queryKey: ["qb-vendors", debounced], queryFn: () => get<QbVendorHit[]>(`/api/integrations/quickbooks/vendors?q=${encodeURIComponent(debounced)}`), enabled: debounced.trim().length >= 2 });
  const link = useMutation({
    mutationFn: (qbId: string) => post(`/api/contacts/${c.id}/quickbooks-vendor/link`, { qbId }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["contact", c.id] });
      onClose();
    },
  });
  return (
    <Dialog title="Link to a QuickBooks vendor" onClose={onClose}>
      <div className="flex flex-col gap-3">
        <p className="text-sm text-ink-soft">Pick {c.displayName}'s vendor in QuickBooks. Only the CRM changes; nothing is written to QuickBooks.</p>
        <Field label="Search QuickBooks vendors" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
        {link.error && <Alert>{errorMessage(link.error)}</Alert>}
        {hits.error && <Alert>{errorMessage(hits.error)}</Alert>}
        {hits.isLoading ? (
          <Loading />
        ) : hits.data && !hits.data.length ? (
          <Empty>No QuickBooks vendor has that in its name.</Empty>
        ) : (
          <ul className="flex flex-col">
            {hits.data?.map((v) => (
              <li key={v.id} className="flex items-center gap-3 border-b border-mist py-2 last:border-0">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">{v.name}</div>
                  <div className="text-sm text-muted">{[v.company !== v.name ? v.company : "", v.linkedTo ? `Linked to ${v.linkedTo.name}` : ""].filter(Boolean).join(" · ")}</div>
                </div>
                <Button className="px-3 py-1.5" disabled={!!v.linkedTo} busy={link.isPending && link.variables === v.id} onClick={() => link.mutate(v.id)}>
                  Link
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
}

type QbVendorRecord = { qbId: string; name: string; company: string; phone: string; email: string; city: string };

/** QuickBooks's vendors with no contact here: tick the ones to bring in (read from QuickBooks only when opened). */
function QbVendorsImport({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["qb-vendors-unlinked"], queryFn: () => get<QbVendorRecord[]>("/api/integrations/quickbooks/vendors/unlinked"), staleTime: 0 });
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [q, setQ] = useState("");
  const shown = (list.data ?? []).filter((v) => !q.trim() || `${v.name} ${v.company}`.toLowerCase().includes(q.trim().toLowerCase()));
  const add = useMutation({
    mutationFn: () => post<{ added: { id: string }[] }>("/api/integrations/quickbooks/vendors/import", { qbIds: [...picked] }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["contacts"] });
      onClose();
    },
  });
  const toggle = (id: string) => setPicked((p) => {
    const n = new Set(p);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    return n;
  });
  return (
    <Dialog
      wide
      title="Vendors from QuickBooks"
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!picked.size} busy={add.isPending} onClick={() => add.mutate()}>
            Add {picked.size || ""} as vendors
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-ink-soft">QuickBooks vendors that aren't contacts here yet. Each one ticked becomes a vendor contact, linked to its QuickBooks vendor, with its phone, email, address and terms. Nothing changes in QuickBooks.</p>
        {list.error && <Alert>{errorMessage(list.error)}</Alert>}
        {add.error && <Alert>{errorMessage(add.error)}</Alert>}
        {list.isLoading ? (
          <Loading />
        ) : !list.data?.length ? (
          <Empty>Every QuickBooks vendor is already a contact here.</Empty>
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-0 flex-1">
                <Field label="Search" value={q} onChange={(e) => setQ(e.target.value)} />
              </div>
              <Button onClick={() => setPicked(new Set([...picked, ...shown.map((v) => v.qbId)]))}>Tick all shown</Button>
              {picked.size > 0 && <Button variant="quiet" onClick={() => setPicked(new Set())}>Clear</Button>}
            </div>
            <ul className="max-h-[50dvh] overflow-auto">
              {shown.map((v) => (
                <li key={v.qbId} className="border-b border-mist last:border-0">
                  <label className="flex cursor-pointer items-center gap-3 py-2">
                    <input type="checkbox" className="size-4" checked={picked.has(v.qbId)} onChange={() => toggle(v.qbId)} />
                    <span className="min-w-0 flex-1">
                      <span className="font-semibold">{v.name}</span>
                      <span className="block truncate text-sm text-muted">{[v.company !== v.name ? v.company : "", v.city, v.phone, v.email].filter(Boolean).join(" · ")}</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </Dialog>
  );
}
